import React, { useContext, useEffect, useRef, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  GetColor,
  getStatus,
  getStatusInfo,
  STATUS_PRIMARY,
} from '../../../../utils';
import { getVariableGroups, getAllVariables } from '../../../../services';
import { images } from '../../../../assets';
import * as s from './styled-produto';

// Ordem de exibicao dos cards por status: rodando -> (atencao) ->
// alarme/bloqueio -> desligada -> sem dados. Atencao (rodando+warning)
// fica logo apos rodando puro. Rank menor = aparece primeiro.
const statusRank = (aplicacao) => {
  const info = getStatusInfo(aplicacao && aplicacao.status);
  if (info.primary === STATUS_PRIMARY.RODANDO) return info.hasAttention ? 1 : 0;
  if (info.primary === STATUS_PRIMARY.ALARME) return 2;
  if (info.primary === STATUS_PRIMARY.DESLIGADA) return 3;
  return 4; // SEM_DADOS
};

const STATUS_BREAKDOWN = [
  { rangeKey: 'O', label: 'rodando' },
  { rangeKey: 'W', label: 'atenção' },
  { rangeKey: 'E', label: 'alarme/erro' },
  { rangeKey: 'OFF', label: 'desligada' },
  { rangeKey: 'NotConnected', label: 'sem dados' },
];

// Soma todas as categorias para descobrir quantas maquinas (TGAs/bombas) a
// aplicacao tem. Quando ha mais de uma maquina, exibimos o breakdown abaixo
// da descricao em vez de so a pill agregada — assim o operador ve quantas
// estao em cada estado sem precisar entrar em "Ver Mais".
const sumMachines = (status) => {
  if (!status) return 0;
  return STATUS_BREAKDOWN.reduce(
    (acc, { rangeKey }) => acc + Number(status[rangeKey] || 0),
    0
  );
};

const FRESH_THRESHOLD_MS = 60 * 1000; // 60s sem update = stale

// Props:
//   hideTotal — quando true, nao renderiza o <Title> com a contagem
//   no fim da grid (usado em /dashboard/customer redesenhado, que ja
//   tem SidebarFooter externo mostrando o mesmo numero).
const AplicacoesCliente = ({ hideTotal = false } = {}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { colors } = useContext(ThemeContext);
  const { mapa, parqueSelecionado } = useSelector(
    (state) => state.segundoNivel
  );
  // Lookup das variables ja cacheadas em Redux iot (transform persiste
  // apos refresh) — usado pelo prefetch on-hover pra pular HTTP redundante.
  const iotByThingName = useSelector((state) => state.iot || {});
  // Tracking de freshness per thing (atualizado pela subscription do
  // primeiro-nivel saga em PRIMEIRO/SET_THING_LAST_SEEN).
  const thingLastSeen = useSelector(
    (state) => (state.primeiroNivel && state.primeiroNivel.thingLastSeen) || {}
  );
  // Re-render a cada 5s pra atualizar o status do chip mesmo quando
  // a subscription nao manda nada (chip vira stale apos threshold).
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 1000), 5000);
    return () => clearInterval(id);
  }, []);
  // Set de thingNames ja prefetchados nesta sessao (alem do cache em
  // Redux, evita disparar request mesmo se o cache estiver chegando).
  const prefetchedRef = useRef(new Set());

  // Prefetch on-hover: o operador costuma hover ~200-400ms antes de
  // clicar num card. Aproveitamos essa janela pra ja buscar as
  // variables da thing em background. Quando o click acontece e a
  // navegacao roda, /usina ja encontra cache hit em iot[thingName]
  // .variables e pula 2 HTTPs (getVariableGroups + getAllVariables) —
  // o chart parte direto pro fetch de historic.
  const prefetchAplicacao = (aplicacao) => {
    const firstThing = aplicacao && aplicacao.thing && aplicacao.thing[0];
    if (!firstThing || !firstThing.id || !firstThing.thing) return;
    const thingName = firstThing.thing;
    if (prefetchedRef.current.has(thingName)) return;
    if (iotByThingName[thingName] && iotByThingName[thingName].variables)
      return;
    prefetchedRef.current.add(thingName);
    // Fire-and-forget. Erros sao silenciosos (proximo fetch real, ja na
    // /usina, vai reportar o erro normalmente).
    getVariableGroups(firstThing.id)
      .then(({ data }) => {
        const supervisorio = (data || [])
          .filter(({ group }) => group === 'supervisorio')
          .pop();
        if (!supervisorio) return undefined;
        return getAllVariables(firstThing.id, 1, 50, supervisorio.id);
      })
      .then((resp) => {
        if (!resp || !resp.data) return;
        dispatch({
          type: 'IOT/SET_VARIABLES_THING',
          variables: resp.data,
          thingName,
        });
      })
      .catch(() => {
        // Permite retry no fetch real
        prefetchedRef.current.delete(thingName);
      });
  };

  // Hover destaca o marker correspondente no mapa (selecao bidirecional
  // via Redux). Antes era click-pra-destacar + click-no-Ver-Mais-pra-
  // navegar — 2 alvos confusos no mesmo cartao. Agora o CLICK no card
  // ja navega direto pra usina; o destaque do marker e passivo via
  // hover, sem precisar de acao deliberada.
  const handleCardHover = (parqueId) => {
    if (!parqueId) return;
    const parque = (mapa && mapa.parques ? mapa.parques : []).find(
      (p) => p.id === parqueId
    );
    if (!parque) return;
    const currentId = parqueSelecionado && parqueSelecionado.id;
    if (currentId === parqueId) return; // ja destacado
    dispatch({
      type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
      parqueSelecionado: parque,
    });
  };

  // Click no card: navega pra usina. State carrega o appliance + dados
  // do parque selecionado. Mantem o comportamento do antigo botao "Ver
  // Mais" mas remove a friccao de ter que mirar num alvo pequeno.
  const handleCardClick = (aplicacao) => {
    history.push('/dashboard/customer/plant', {
      ...aplicacao,
      ...parqueSelecionado,
    });
  };

  // Achata SEMPRE todas as aplicacoes de TODOS os parques visiveis,
  // anotando cada uma com parqueIdLink pra correlacionar com o marker
  // clicado no mapa. Antes filtravamos por parqueSelecionado.appliance
  // mas isso ESCONDIA cards — agora a UX e destacar (selected style)
  // em vez de ocultar (operador ve a frota inteira e qual e a do
  // marker clicado).
  const parquesData = mapa && mapa.parques ? mapa.parques : [];
  // Ordena por status (statusRank): rodando -> atencao -> alarme ->
  // desligada -> sem dados. sort estavel preserva a ordem original entre
  // cards de mesmo status.
  const filteredApps = parquesData
    .flatMap((parque) =>
      (parque.appliance || []).map((a) => ({ ...a, parqueIdLink: parque.id }))
    )
    .sort((a, b) => statusRank(a) - statusRank(b));
  const selectedParqueId =
    parqueSelecionado && parqueSelecionado.id ? parqueSelecionado.id : null;
  const totalAplicacoes = filteredApps.length;
  const aplicacoesLabel =
    totalAplicacoes === 1 ? 'Aplicação cadastrada' : 'Aplicações cadastradas';

  return (
    <s.Container>
      <s.AppsGrid>
        {filteredApps.length > 0 ? (
          filteredApps.map((aplicacao) => {
            const isSelected =
              selectedParqueId != null &&
              aplicacao.parqueIdLink === selectedParqueId;
            // Freshness: usa o ts da PRIMEIRA thing do appliance — proxy
            // razoavel pra "este card recebeu update recente". null se
            // subscription ainda nao mandou nada.
            const firstThingName =
              aplicacao.thing && aplicacao.thing[0] && aplicacao.thing[0].thing;
            const lastSeen = firstThingName
              ? thingLastSeen[firstThingName]
              : null;
            const isFresh =
              lastSeen && Date.now() - lastSeen < FRESH_THRESHOLD_MS;

            // Info extra do card: tipo + count + metricas realtime.
            // Tipo (ucha/bomba) determina label + cor accent (paleta
            // padronizada: UCHA=laranja, EBA=azul).
            const isUcha = aplicacao.type && aplicacao.type.type === 'ucha';
            const machineCount = (aplicacao.thing && aplicacao.thing.length) || 0;
            const typeLabel = isUcha
              ? machineCount === 1 ? '1 TGA' : `${machineCount} TGAs`
              : machineCount === 1 ? '1 Bomba' : `${machineCount} Bombas`;
            const typeColor = isUcha ? colors.orange : colors.blue;

            // Metricas realtime: potencia e vazao da primeira thing.
            // state.iot[thingName].values vem da subscription onUpdateIOT
            // (primeiro-nivel saga). Pode estar ausente se subscription
            // nao mandou nada ainda — entao mostramos so quando ha valor.
            const sumStatus = sumMachines(aplicacao.status);
            const runningCount = Number(aplicacao.status?.O || 0);

            // Status exibido no card = status agregado do backend
            // (getStatus cobre E>alarme, W>atencao, O/N>rodando,
            // OFF>desligada). BUG anterior: o card sobrescrevia esse
            // status com `isOn` (Sta_Rodando do device, vindo do realtime
            // segundos apos o load), FORCANDO "RODANDO" — entao um
            // appliance em alarme/atencao "trocava" pra rodando ao
            // carregar. Removido o override: a classificacao do backend e
            // a fonte de verdade (ja reflete rodando quando esta rodando),
            // sem flip.
            const displayStatusLabel = aplicacao.status
              ? getStatus(aplicacao.status)
              : '';
            const displayStatusColor = GetColor(displayStatusLabel);
            const hasStatusTag = !!aplicacao.status;
            return (
              <s.CardWrapper
                key={aplicacao.id}
                selected={isSelected}
                onClick={() => handleCardClick(aplicacao)}
                onMouseEnter={() => {
                  handleCardHover(aplicacao.parqueIdLink);
                  prefetchAplicacao(aplicacao);
                }}
                clickable
                title="Clique para abrir a usina"
              >
                <s.Line width="100%" justify="space-between">
                  {hasStatusTag && (
                    <s.Tag color={displayStatusColor} selected>
                      <s.FreshnessDot
                        $live={isFresh}
                        title={
                          lastSeen
                            ? `Última leitura: ${new Date(lastSeen).toLocaleTimeString('pt-BR')}`
                            : 'Sem dados em tempo real ainda'
                        }
                      />
                      <label style={{ cursor: 'default' }}>
                        {displayStatusLabel}
                      </label>
                    </s.Tag>
                  )}
                  {!!(aplicacao.status && aplicacao.status.E) && (
                    <s.Tag color={GetColor('Alarme/erro')} selected>
                      <label style={{ cursor: 'default' }}>
                        {aplicacao.status.E}
                      </label>
                    </s.Tag>
                  )}
                </s.Line>
                <s.CardImage
                  key={aplicacao.id}
                  src={
                    aplicacao.type.type === 'ucha'
                      ? images.UchaAplicacao
                      : images.BombaAplicacao
                  }
                />
                <s.Label>{aplicacao.description}</s.Label>

                {/* Tipo + count: TGA (laranja) / Bomba (azul) — paleta
                    padronizada que reforca a familia da maquina. */}
                <s.TypeBadge color={typeColor}>
                  {isUcha ? (
                    /* lucide:zap (energia) */
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                  ) : (
                    /* lucide:droplet (agua) */
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" />
                    </svg>
                  )}
                  {typeLabel}
                </s.TypeBadge>

                {/* Resumo de status quando ha mais de uma maquina (ex.: 2/3
                    rodando). Para 1 maquina, a Tag de status no topo ja
                    diz tudo. */}
                {sumStatus > 1 && (
                  <s.Metrics title={`${runningCount} de ${sumStatus} rodando`}>
                    <s.Metric color={GetColor('rodando')}>
                      {/* lucide:activity */}
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                      </svg>
                      {runningCount}
                      <small>/{sumStatus}</small>
                    </s.Metric>
                  </s.Metrics>
                )}

                {sumMachines(aplicacao.status) > 1 && (
                  <s.Breakdown>
                    {STATUS_BREAKDOWN.map(({ rangeKey, label }) => {
                      const count = Number(aplicacao.status?.[rangeKey] || 0);
                      if (count === 0) return null;
                      return (
                        <s.BreakdownChip
                          key={rangeKey}
                          title={`${count} ${label}`}
                        >
                          <s.BreakdownDot color={GetColor(label)} />
                          {count}
                        </s.BreakdownChip>
                      );
                    })}
                  </s.Breakdown>
                )}
              </s.CardWrapper>
            );
          })
        ) : (
          <s.NoApplicationMessage>Não há Aplicações</s.NoApplicationMessage>
        )}
      </s.AppsGrid>
      {totalAplicacoes > 0 && !hideTotal && (
        <s.Title>{`${totalAplicacoes} ${aplicacoesLabel}`}</s.Title>
      )}
    </s.Container>
  );
};

export default AplicacoesCliente;
